// Excel export for the Data tab, laid out like the "RD1 Data Export" sheet the program team
// signed off on: title rows, then one table per section with a blue header row, Total rows, and
// formulas for rates, percentages and totals. Each block only references its own rows, so a
// single section exported on its own still calculates.
//
// The label column is LABEL_COLS narrow columns merged together, so the Organizations bar chart can
// be drawn from cells: each segment spans a share of those columns proportional to its count.

import { DONATION_PER_DRIVE, MAX_DONATION_PER_EVENT, MAX_DRIVES_PER_EVENT } from "src/utils/eventStats";

export type ExportBlock = "requests" | "orgs" | "funding" | "fbt" | "donations" | "reports";

export interface DataExportInput {
  seasonName: string;
  exportedBy: string;
  exportedAt: Date;
  requests: { total: number; approved: number; new: number; ineligible: number; cancelled: number };
  firstTime: { requests: number; approved: number };
  hispanic: { requests: number; approved: number };
  orgs: { school: number; community: number; unclassified: number };
  subcategories: { type: string; subcategory: string; count: number }[];
  subcategoryUnclassified: number;
  funding: { category: string; count: number }[];
  fbt: { partner: string; count: number }[];
  donations: { checksSent: number; totalSent: number; atMaxCount: number; avgValidDrives: number | null; avgEligibleDrives: number | null };
  reapplications: { reapplied: number; approved: number; declined_again: number; pending: number } | null;
  multiEvent: { dealers: number; events: number };
}

const NAVY = "#0A0D6B";
const GRAY = "#555A6E";
const BLUE = "#1A6FE0";
const RULE = { color: "#D5D8E0", size: 1 };
const FONT = "Arial";

// Same series colors as the Data tab's split bar
const SCHOOL = "#1D6CE0";
const COMMUNITY = "#C2550C";
const UNCLASSIFIED = "#D5D8DD";

const LABEL_COLS = 40;
const LABEL_COL_WIDTH = 11; // px; LABEL_COLS * this = the old 440px label column
// Excel letters of the value columns that follow the merged label columns
const colLetter = (index: number) =>
  (index >= 26 ? String.fromCharCode(64 + Math.floor(index / 26)) : "") + String.fromCharCode(65 + (index % 26));
const B = colLetter(LABEL_COLS);
const C = colLetter(LABEL_COLS + 1);
// px; the label columns, then B, C and the notes column
const COL_WIDTHS = [ ...Array(LABEL_COLS).fill(LABEL_COL_WIDTH), 80, 105, 290 ];

const NUM = "#,##0";
const USD = '"$"#,##0';
const AVG = "#,##0.0";
const PCT = "0.0%";

type Cell = { value?: string | number | null; formula?: string; format?: string; color?: string; textAlign?: string; colSpan?: number };

const fullDate = (d: Date) =>
  d.toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" })
    .replace(/,(?=\s\d{4})/, ""); // "Mon, Oct 26 2026, 3:10 PM"

export function buildDataExportWorkbook(input: DataExportInput, blocks: ExportBlock[]) {
  const rows: any[] = [];
  const at = () => rows.length + 1; // 1-based Excel row the next push lands on

  const styled = (cell: Cell, base: object) => ({
    fontFamily: FONT, fontSize: 11, textAlign: "left", ...base,
    ...(cell.format ? { format: cell.format } : {}),
    ...(cell.textAlign ? { textAlign: cell.textAlign } : {}),
    ...(cell.color ? { color: cell.color } : {}),
    ...(cell.colSpan ? { colSpan: cell.colSpan } : {}),
    ...(cell.formula ? { formula: cell.formula } : { value: cell.value ?? null }),
  });

  const header = (...labels: string[]) =>
    rows.push({
      cells: labels.map((label, i) => styled({ value: label, colSpan: i ? undefined : LABEL_COLS }, { color: "#FFFFFF", background: BLUE })),
    });

  // label across the merged label columns, then values in B.. ; every body row gets the thin rule
  // like the template
  const body = (label: string, ...cells: Cell[]) =>
    rows.push({
      cells: [
        styled({ value: label, colSpan: LABEL_COLS }, { borderBottom: RULE, wrap: true }),
        ...cells.map((c) => styled(c, { borderBottom: RULE })),
      ],
    });

  // Total rows: numbers right-justified so they stand out from the left-aligned body rows
  const totalRow = (label: string, ...cells: Cell[]) => body(label, ...cells.map((c) => ({ ...c, textAlign: "right" })));

  const blank = () => rows.push({ cells: [] });

  // ---- title
  rows.push({ height: 26, cells: [ styled({ value: "Drive For Your Community: Data Export", colSpan: LABEL_COLS }, { color: NAVY, fontSize: 16 }) ] });
  rows.push({
    cells: [ styled({
      value: `Program year ${input.seasonName}  |  Exported ${fullDate(input.exportedAt)}  |  Exported by: ${input.exportedBy}`,
    }, { color: GRAY }) ],
  });
  blank();

  const note = (text: string): Cell => ({ value: text, color: GRAY });

  if (blocks.includes("requests")) {
    const r = input.requests;
    header("Event Requests", "Value");
    const first = at();
    body("Approved", { value: r.approved, format: NUM });
    body("New", { value: r.new, format: NUM });
    body("Ineligible", { value: r.ineligible, format: NUM });
    body("Cancelled", { value: r.cancelled, format: NUM });
    totalRow("Total", { formula: `SUM(${B}${first}:${B}${at() - 1})`, format: NUM });
    blank();

    header("Dealer Groups", "Requests", "Approved", "Approval rate");
    body("U300 Dealers", {}, {}, note("FOR 2027: no data until the 2027 program"));
    const group = (label: string, g: { requests: number; approved: number }) => {
      const row = at();
      body(label, { value: g.requests, format: NUM }, { value: g.approved, format: NUM },
        { formula: `IF(${B}${row}=0,"-",${C}${row}/${B}${row})`, format: PCT });
    };
    group("First Time Dealers", input.firstTime);
    group("Hispanic Dealers", input.hispanic);
    blank();
  }

  // Events + % of the block's own Total row (= approved events). A string item is a bold subheader
  // row with no values; SUM skips its empty cells.
  const shareBlock = (title: string, items: ([string, number] | string)[]) => {
    header(title, "Events", "% of approved");
    const first = at();
    const total = first + items.length;
    items.forEach((item) => {
      if (typeof item === "string") {
        rows.push({ cells: [ styled({ value: item, colSpan: LABEL_COLS }, { bold: true, borderBottom: RULE }), styled({}, { borderBottom: RULE }), styled({}, { borderBottom: RULE }) ] });
        return;
      }
      const [ label, count ] = item;
      const row = at();
      body(label, { value: count, format: NUM }, { formula: `IF($${B}$${total}=0,0,${B}${row}/$${B}$${total})`, format: PCT });
    });
    totalRow("Total", { formula: `SUM(${B}${first}:${B}${total - 1})`, format: NUM }, { formula: `SUM(${C}${first}:${C}${total - 1})`, format: PCT });
  };

  // Stacked 100% bar drawn with cells, like the page's split bar: title, total, a legend row with a
  // color swatch + label per segment, then one row whose segments span columns in proportion to
  // their counts. Spans use largest remainders so they always fill the label columns exactly, and
  // any non-zero segment gets at least one column.
  const barChart = (title: string, segments: { label: string; count: number; color: string }[]) => {
    const total = segments.reduce((sum, seg) => sum + seg.count, 0);
    rows.push({ cells: [ styled({ value: title, colSpan: LABEL_COLS }, { bold: true, fontSize: 12 }) ] });
    rows.push({ cells: [ styled({ value: `Approved Events (${total})`, colSpan: LABEL_COLS }, {}) ] });
    blank();
    // Legend on one row: swatch, then the label merged across as many columns as its text needs
    // (about 6px a character at 11pt, plus a gap), running into the value columns if needed
    let col = 0;
    rows.push({
      cells: segments.flatMap((seg) => {
        const value = `${seg.label} (${seg.count})`;
        const start = col + 1;
        let end = start;
        for (let px = 0; px < value.length * 6 + 8 && end < COL_WIDTHS.length; end++) px += COL_WIDTHS[end];
        col = end;
        return [
          styled({}, { background: seg.color }),
          styled({ value, colSpan: end - start > 1 ? end - start : undefined }, {}),
        ];
      }),
    });
    if (!total) return;
    blank();

    const visible = segments.filter((seg) => seg.count > 0);
    const exact = visible.map((seg) => (seg.count / total) * LABEL_COLS);
    const spans = exact.map((x) => Math.max(1, Math.floor(x)));
    let left = LABEL_COLS - spans.reduce((a, b) => a + b, 0);
    const byRemainder = exact.map((x, i) => i).sort((a, b) => (exact[b] % 1) - (exact[a] % 1));
    for (let k = 0; left > 0; k = (k + 1) % byRemainder.length, left--) spans[byRemainder[k]]++;
    for (let k = byRemainder.length - 1; left < 0; k = (k - 1 + byRemainder.length) % byRemainder.length) {
      if (spans[byRemainder[k]] > 1) { spans[byRemainder[k]]--; left++; }
    }

    rows.push({
      height: 22,
      cells: visible.map((seg, i) => styled({
        // text, not a number, so a narrow segment clips instead of showing ####
        value: seg.count / total >= 0.08 ? `${((seg.count / total) * 100).toFixed(1)}%` : "",
        colSpan: spans[i] > 1 ? spans[i] : undefined,
        textAlign: "center",
      }, { background: seg.color, color: seg.color === UNCLASSIFIED ? "#333333" : "#FFFFFF", verticalAlign: "center" })),
    });
  };

  if (blocks.includes("orgs")) {
    const o = input.orgs;
    shareBlock("Organizations (approved events)", [
      [ "Schools/Youth Programs", o.school ],
      [ "Community Organizations", o.community ],
      ...(o.unclassified ? [ [ "Not classified yet (approved before classification)", o.unclassified ] as [string, number] ] : []),
    ]);
    blank();
    barChart("Schools/Youth Programs vs. Community Organizations", [
      { label: "Schools/Youth Programs", count: o.school, color: SCHOOL },
      { label: "Community Organizations", count: o.community, color: COMMUNITY },
      ...(o.unclassified ? [ { label: "Not classified yet", count: o.unclassified, color: UNCLASSIFIED } ] : []),
    ]);
    blank();
    const unclassified = input.subcategoryUnclassified;
    shareBlock("Organization Subcategories (approved events)", [
      // a subheader before each organization type's subcategories (D4URX-2768)
      ...input.subcategories.flatMap((sub, i) => [
        ...(i === 0 || input.subcategories[i - 1].type !== sub.type ? [ sub.type ] : []),
        [ sub.subcategory, sub.count ] as [string, number],
      ]),
      ...(unclassified ? [ [ "Not classified yet (approved before classification)", unclassified ] as [string, number] ] : []),
    ]);
    blank();
  }

  if (blocks.includes("funding")) {
    shareBlock("Funding Purpose Categories (approved events)", input.funding.map((f) => [ f.category, f.count ] as [string, number]));
    blank();
  }

  if (blocks.includes("fbt")) {
    // % of all approved events, like the page; most approved events have no FBT partner, so the
    // block's own Total row is not the denominator
    const approved = input.requests.approved;
    header("FBT Partners (approved events)", "Events", "% of approved");
    const first = at();
    input.fbt.forEach(({ partner, count }) => {
      const row = at();
      body(partner, { value: count, format: NUM }, { formula: approved ? `${B}${row}/${approved}` : "0", format: PCT });
    });
    const totalAt = at();
    totalRow("Total FBT partner events", { formula: `SUM(${B}${first}:${B}${totalAt - 1})`, format: NUM }, { formula: `SUM(${C}${first}:${C}${totalAt - 1})`, format: PCT });
    const fbtTotal = input.fbt.reduce((sum, f) => sum + f.count, 0);
    rows.push({ cells: [ styled({ value: `${fbtTotal} of the ${approved} approved events have an FBT partner designated as their primary beneficiary.`, colSpan: LABEL_COLS + 3 }, { color: GRAY }) ] });
    blank();
  }

  if (blocks.includes("donations")) {
    const d = input.donations;
    header("Donations", "Value");
    body("Donation per valid test drive", { value: DONATION_PER_DRIVE, format: USD }, {}, note("Program rule"));
    body("Maximum donation per event", { value: MAX_DONATION_PER_EVENT, format: USD }, {}, note(`Program rule (reached at ${MAX_DRIVES_PER_EVENT} test drives)`));
    const checksRow = at();
    body("Event donations approved (checks sent)", { value: d.checksSent, format: NUM });
    const sentRow = at();
    body("Total donation amount sent", { value: d.totalSent, format: USD });
    body("Avg valid test-drives per event",
      d.avgValidDrives === null ? { value: "N/A" } : { value: Math.round(d.avgValidDrives * 10) / 10, format: AVG }, {},
      note("Average valid test drives per event with a donation requested or approved. Not capped."));
    body("Avg donation-eligible test-drives per event",
      d.avgEligibleDrives === null ? { value: "N/A" } : { value: Math.round(d.avgEligibleDrives * 10) / 10, format: AVG }, {},
      note(`Average test drives per event that count toward the donation, capped at ${MAX_DRIVES_PER_EVENT} per event (${"$" + MAX_DONATION_PER_EVENT.toLocaleString("en-US")} max).`));
    body("Avg. donation per event", { formula: `IF(${B}${checksRow}=0,0,${B}${sentRow}/${B}${checksRow})`, format: USD });
    body("Events at max donation", { value: d.atMaxCount, format: NUM }, {},
      note(`Number of events that reached the ${"$" + MAX_DONATION_PER_EVENT.toLocaleString("en-US")} max donation (${MAX_DRIVES_PER_EVENT}+ valid test drives).`));
    blank();
  }

  if (blocks.includes("reports")) {
    const re = input.reapplications;
    header("Automated Reports", "Value");
    if (re) {
      // no separate "Declined dealers who reapplied" row: it always equalled the Total
      const first = at();
      body("Reapplied, approved", { value: re.approved, format: NUM });
      body("Reapplied, declined again", { value: re.declined_again, format: NUM });
      body("Reapplied, pending", { value: re.pending, format: NUM });
      totalRow("Total declined dealers who reapplied", { formula: `SUM(${B}${first}:${B}${at() - 1})`, format: NUM });
    } else {
      body("Declined dealers who reapplied", { value: "n/a" }, {}, note("Could not load"));
    }
    blank();
    body("Dealers hosting 2+ events", { value: input.multiEvent.dealers, format: NUM });
    // A plain value, not a formula: Numbers leaves this IF/TEXT formula blank
    const { dealers, events } = input.multiEvent;
    body("  Events from those dealers", { value: events, format: NUM },
      { value: dealers ? `(${(events / dealers).toFixed(1)} each)` : "" });
  }

  return {
    sheets: [ {
      name: "Summary",
      showGridLines: false,
      // pixel widths matching the template's column widths (62 / 9.4 / 14 / 39.6 characters), with
      // the 440px label column split into LABEL_COLS narrow columns
      columns: COL_WIDTHS.map((width) => ({ width })),
      rows,
    } ],
  };
}
